import { useState, useEffect } from 'react';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Organization } from '../models/Organization';

export function useOrganizations() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setLoading(true);
    /** Top-level accounts live under `clients/`; legacy per-org projects stayed as docs with entityType project_legacy. */
    const isTopLevelClientRow = (data: Record<string, unknown>): boolean => {
      const et = data.entityType;
      return et !== 'project' && et !== 'project_legacy';
    };

    const unsubscribeClients = onSnapshot(
      collection(db, 'clients'),
      (snapshot) => {
        const orgsData = snapshot.docs
          .filter((docSnap) => isTopLevelClientRow(docSnap.data() as Record<string, unknown>))
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              clientIds: data.clientIds || data.projectIds || [],
              projectIds: data.projectIds || data.clientIds || [],
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date(),
            } as Organization;
          });

        setOrganizations(orgsData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching client-entities:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => {
      unsubscribeClients();
    };
  }, []);

  // Helper function to convert Date objects to Timestamps recursively
  const convertDatesToTimestamps = (obj: any): any => {
    if (obj === null || obj === undefined) return obj;
    if (obj instanceof Date) return Timestamp.fromDate(obj);
    if (Array.isArray(obj)) return obj.map(convertDatesToTimestamps);
    if (typeof obj === 'object') {
      const converted: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          converted[key] = convertDatesToTimestamps(obj[key]);
        }
      }
      return converted;
    }
    return obj;
  };

  const createOrganization = async (orgData: Omit<Organization, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = Timestamp.now();
      // Convert any Date objects to Timestamps
      const convertedData = convertDatesToTimestamps(orgData);
      await addDoc(collection(db, 'clients'), {
        ...convertedData,
        entityType: 'client',
        projectIds: orgData.clientIds || [],
        createdAt: now,
        updatedAt: now,
      });
    } catch (err) {
      console.error('Error creating organization:', err);
      throw err;
    }
  };

  const updateOrganization = async (orgId: string, updates: Partial<Organization>) => {
    try {
      const orgRef = doc(db, 'clients', orgId);
      await updateDoc(orgRef, {
        ...updates,
        entityType: 'client',
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('Error updating organization:', err);
      throw err;
    }
  };

  const deleteOrganization = async (orgId: string) => {
    try {
      const orgRef = doc(db, 'clients', orgId);
      await deleteDoc(orgRef);
    } catch (err) {
      console.error('Error deleting organization:', err);
      throw err;
    }
  };

  return {
    organizations,
    loading,
    error,
    createOrganization,
    updateOrganization,
    deleteOrganization,
  };
}
